import { CheckLimitsComponentModule } from '@b2b/common/components/check-limits';
import { ReservationInputComponentModule } from '@b2b/common/components/reservation-input';
import { input, LooseAutocomplete, output, TODO } from '@b2b/types/core';
import { TicketConversionStatus } from '@b2b/types/order/api/enums';
import { WillCallOrderItemsByEventVo } from '@b2b/types/order/api/will-call-order-detail.type';
import { OrderItem } from '@b2b/types/order/order-item.type';
import { OrderEventItems } from '@b2b/types/order/order.type';
import uiRouter from '@uirouter/angularjs';
import angular from 'angular';
import { normalizeQuantity, shouldShowDiscountedPriceLink, shouldShowFullPriceLink } from './../../utils/order-validation.utils';
import { calculateOriginalSubtotal } from '../../../utils';

interface OrderServiceType {
  getItemType: (type: string, subType?: string) => string;
}

interface EventItem extends OrderEventItems {
  eventId: LooseAutocomplete<'membership' | 'addons'>;
  toggleCSRPrices?: boolean;
  toggleCSRAddons?: boolean;
}

class ExistingOrderEventTableController {
  static $inject = ['$element', 'OrderService'];

  event = input<EventItem>()!;
  hasMembershipConversion = input(false);
  eventHasAddOns = input(false);
  originalOrder = input<{ eventItems: WillCallOrderItemsByEventVo[] }>();
  isOrganizationOrder = input<boolean>();
  membershipId = input<string | null>();
  findTicketDiscounted = input<(eventId: string, ticketTypeId: string) => { discountedPrice: string } | null>();
  checkingLimits = input(false);

  onItemQuantityChanged = output<{ item: OrderItem; quantity: number }>();
  onReservationError = output<{ $error: string }>();
  onReservationSuccess = output<{ item: OrderItem }>();
  onRemoveItemType = output<{ event: EventItem; item: OrderItem }>();
  onRemoveItemQuantity = output<{ item: OrderItem }>();
  onRestoreItemQuantity = output<{ item: OrderItem }>();
  onAddFullPriceItem = output<{ event: EventItem; item: OrderItem; itemIndex: number }>();
  onAddDiscountedPriceItem = output<{ event: EventItem; item: OrderItem; itemIndex: number }>();
  onCheckLimits = output<void>();

  constructor(
    private $element: JQuery,
    private orderService: OrderServiceType,
  ) {}

  $onInit() {
    this.$element.addClass('acme-existing-order-event-table');
  }

  isEventEditable(event: { eventId: LooseAutocomplete<'membership'> }): boolean {
    if (event.eventId.toLowerCase() === 'membership') {
      return false;
    }
    return true;
  }

  isAddon(event: { eventId: LooseAutocomplete<'addons'> }): boolean {
    return event.eventId.toLowerCase() === 'addons';
  }

  isEventItemType(type: LooseAutocomplete<'Event' | 'Combo'>): boolean {
    return type === 'Event' || type === 'Combo';
  }

  isInventoryItemType(type: LooseAutocomplete<'Inventory' | 'ComboInventory'>): boolean {
    return type === 'Inventory' || type === 'ComboInventory';
  }

  isMembershipConversion(ticket: { conversionStatus: TicketConversionStatus }): boolean {
    return ticket && ticket.conversionStatus && ticket.conversionStatus === 'MembershipConversion';
  }

  getItemType(itemType: string): string {
    return this.orderService.getItemType(itemType);
  }

  calculatePrice(ticket: { quantity: string | number; price: TODO; discountedUnitPrice: number }): number {
    const quantity = normalizeQuantity(ticket.quantity);
    ticket.quantity = quantity;
    ticket.price = quantity * ticket.discountedUnitPrice;

    return ticket.price;
  }

  calculateAddOnPrice(addOn: { quantity: string | number; discountedUnitPrice: number }): number {
    const quantity = normalizeQuantity(addOn.quantity);
    const price = quantity * addOn.discountedUnitPrice;
    return price || 0;
  }

  calculateOriginalSubtotal(item: { orderItemId?: string; resolvedQuantity?: string | number; discountedUnitPrice: number }): number {
    if (!this.originalOrder) {
      return 0;
    }
    return calculateOriginalSubtotal(this.originalOrder, item.orderItemId, this.event.eventId);
  }

  handleReservationError($error: string): void {
    this.onReservationError({ $event: { $error } });
  }

  handleReservationSuccess(item: OrderItem): void {
    this.onReservationSuccess({ $event: { item } });
  }

  handleUpdateQuantity(item: OrderItem, quantity: number): void {
    this.onItemQuantityChanged({ $event: { item, quantity } });
  }

  handleRemoveItemType(item: OrderItem): void {
    this.onRemoveItemType({ $event: { event: this.event, item } });
  }

  handleRemoveItemQuantity(item: OrderItem): void {
    this.onRemoveItemQuantity({ $event: { item } });
  }

  handleRestoreItemQuantity(item: OrderItem): void {
    this.onRestoreItemQuantity({ $event: { item } });
  }

  handleAddFullPriceItem(item: OrderItem, itemIndex: number): void {
    this.onAddFullPriceItem({ $event: { event: this.event, item, itemIndex } });
  }

  shouldShowFullPriceLink(item: OrderItem): boolean {
    if (this.isMembershipConversion({ conversionStatus: item.conversionStatus as TicketConversionStatus }) || !this.isEventEditable(this.event)) {
      return false;
    }

    return shouldShowFullPriceLink(item, this.isOrganizationOrder);
  }

  handleAddDiscountedPriceItem(item: OrderItem, itemIndex: number): void {
    this.onAddDiscountedPriceItem({ $event: { event: this.event, item, itemIndex } });
  }

  shouldShowDiscountedPriceLink(item: OrderItem): boolean {
    if (this.isMembershipConversion({ conversionStatus: item.conversionStatus as TicketConversionStatus }) || !this.isEventEditable(this.event)) {
      return false;
    }
    
    return shouldShowDiscountedPriceLink(item, {
      membershipId: this.membershipId,
      eventId: this.event.eventId,
      findTicketDiscounted: this.findTicketDiscounted,
    });
  }
}

export const ExistingOrderEventTableComponentModule = angular
  .module('acme.view.application.orders.edit.components.existing-order-event-table', [
    uiRouter,
    'acme.common.service.order',
    'acme.common.directive.counter',
    'acme.common.directive.minus',
    'acme.common.filter.saleChannel',
    CheckLimitsComponentModule,
    ReservationInputComponentModule,
  ])
  .component('acmeExistingOrderEventTable', {
    templateUrl: `views/application/views/orders/edit/components/existing-order-event-table/existing-order-event-table.component.tpl.html`,
    controller: ExistingOrderEventTableController,
    controllerAs: '$ctrl',
    bindings: {
      event: '<',
      eventHasAddOns: '<',
      hasMembershipConversion: '<',
      originalOrder: '<',
      isOrganizationOrder: '<',
      membershipId: '<',
      findTicketDiscounted: '<',

      onItemQuantityChanged: '&',
      onReservationError: '&',
      onReservationSuccess: '&',
      onRemoveItemType: '&',
      onRemoveItemQuantity: '&',
      onRestoreItemQuantity: '&',
      onAddFullPriceItem: '&',
      onAddDiscountedPriceItem: '&',
      onCheckLimits: '&',
      checkingLimits: '<',
    },
  }).name;
